import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent } from '@ohif/ui-next';
import { X } from 'lucide-react';

import { ToggleSwitch } from '../common';
import { authService } from '../../auth/authService';
import facilityService from '../../services/facilityService';
import type { CreateDicomRoutingRuleRequest } from '../../services/dicomRoutingRuleService';
import type { StudyAssignmentRule } from '../../services/studyAssignmentRuleService';
import type { LinkedHubFacilityDetail } from '../../types/facility.types';
import {
  findRoutingRuleModalityConflict,
  hasPrefetchedCrossRuleValidationData,
} from '../../utils/risRuleModalityConflictUtils';
import {
  fetchAndStoreModalities,
  formatModalityDisplayLabel,
  getModalities,
  getRoutingRuleModalityOptions,
  type ModalityOption,
} from '../../utils/modalityUtils';
import {
  getRadiologyTestsWithPrices,
  type RadiologyTestWithPrice,
} from '../../services/radiologyTestService';
import { ICON_STROKE_WIDTH, Z_INDEX_CHANGE_PASSWORD_MODAL } from '../../constants';
import './AddRoutingRuleModal.css';

export interface AddRoutingRuleFormValues {
  name: string;
  modalityIds: number[];
  studyTypeIds: number[];
  aeTitle: string;
  hubFacilityId: number | null;
  matchPriority: string;
  destinationType: string;
  destinationAe: string;
  active: boolean;
}

export interface AddRoutingRuleSubmitMeta {
  studyTypeLabels: string[];
  hubFacilityName?: string;
}

interface AddRoutingRuleModalProps {
  isOpen: boolean;
  /** Prefetched on the routing rules page; null skips frontend validation. */
  activeStudyAssignmentRules?: StudyAssignmentRule[] | null;
  onClose: () => void;
  onSubmit: (
    payload: CreateDicomRoutingRuleRequest,
    meta: AddRoutingRuleSubmitMeta
  ) => Promise<void>;
}

const MATCH_PRIORITY_OPTIONS = [
  { value: '', label: 'Any priority' },
  { value: 'ROUTINE', label: 'Routine' },
  { value: 'URGENT', label: 'Urgent' },
  { value: 'STAT', label: 'STAT' },
];

const DESTINATION_TYPE_OPTIONS = [
  { value: 'PACS', label: 'PACS' },
  { value: 'VNA', label: 'VNA' },
  { value: 'ARCHIVE', label: 'Archive' },
];

const DEFAULT_FORM: AddRoutingRuleFormValues = {
  name: '',
  modalityIds: [],
  studyTypeIds: [],
  aeTitle: '',
  hubFacilityId: null,
  matchPriority: '',
  destinationType: 'PACS',
  destinationAe: 'PACS_PRIMARY',
  active: true,
};

function RoutingModalityPills({
  loadingModalities,
  modalityOptions,
  selectedModalityIds,
  onToggle,
}: Readonly<{
  loadingModalities: boolean;
  modalityOptions: ModalityOption[];
  selectedModalityIds: number[];
  onToggle: (modalityId: number) => void;
}>) {
  if (loadingModalities) {
    return <span className="add-routing-rule-muted">Loading modalities…</span>;
  }
  if (modalityOptions.length === 0) {
    return <span className="add-routing-rule-muted">No modalities available</span>;
  }
  return (
    <>
      {modalityOptions.map(option => {
        const selected = selectedModalityIds.includes(option.id);
        return (
          <button
            key={option.id}
            type="button"
            className={['add-routing-rule-modality-pill', selected ? 'selected' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onToggle(option.id)}
          >
            {option.label}
          </button>
        );
      })}
    </>
  );
}

function RoutingStudyTypesField({
  studyTypesOpen,
  onToggleOpen,
  selectedStudyLabels,
  studyTypeSearch,
  onStudyTypeSearchChange,
  loadingTests,
  modalityIds,
  filteredRadiologyTests,
  selectedStudyTypeIds,
  onToggleStudyType,
}: Readonly<{
  studyTypesOpen: boolean;
  onToggleOpen: () => void;
  selectedStudyLabels: string[];
  studyTypeSearch: string;
  onStudyTypeSearchChange: (value: string) => void;
  loadingTests: boolean;
  modalityIds: number[];
  filteredRadiologyTests: RadiologyTestWithPrice[];
  selectedStudyTypeIds: number[];
  onToggleStudyType: (testId: number) => void;
}>) {
  let optionsContent: React.ReactNode;
  if (loadingTests) {
    optionsContent = (
      <div className="add-routing-rule-study-option muted">Loading study types…</div>
    );
  } else if (modalityIds.length === 0) {
    optionsContent = (
      <div className="add-routing-rule-study-option muted">
        Select a modality to load study types
      </div>
    );
  } else if (filteredRadiologyTests.length === 0) {
    optionsContent = (
      <div className="add-routing-rule-study-option muted">No study types found</div>
    );
  } else {
    optionsContent = filteredRadiologyTests.map(test => {
      const selected = selectedStudyTypeIds.includes(test.id);
      return (
        <button
          key={test.id}
          type="button"
          className={['add-routing-rule-study-option', selected ? 'selected' : '']
            .filter(Boolean)
            .join(' ')}
          onClick={() => onToggleStudyType(test.id)}
        >
          <span className="add-routing-rule-study-option-name">{test.test_name}</span>
          {test.modality ? (
            <span className="add-routing-rule-study-option-modality">
              {formatModalityDisplayLabel(test.modality)}
            </span>
          ) : null}
        </button>
      );
    });
  }

  return (
    <div className="add-routing-rule-field">
      <label
        className="add-routing-rule-label"
        htmlFor="routing-rule-study-types"
      >
        Study types <span className="add-routing-rule-optional">(optional)</span>
      </label>
      <div className="add-routing-rule-study-types">
        <button
          id="routing-rule-study-types"
          type="button"
          className={['add-routing-rule-study-trigger', studyTypesOpen ? 'open' : '']
            .filter(Boolean)
            .join(' ')}
          onClick={onToggleOpen}
        >
          {selectedStudyLabels.length > 0 ? (
            <span className="add-routing-rule-study-summary">{selectedStudyLabels.join(', ')}</span>
          ) : (
            <span className="add-routing-rule-placeholder">
              CT Brain, CT Chest (comma-separated)
            </span>
          )}
        </button>
        {studyTypesOpen ? (
          <div className="add-routing-rule-study-dropdown">
            <input
              type="text"
              className="add-routing-rule-study-search"
              placeholder="Search study types"
              value={studyTypeSearch}
              onChange={e => onStudyTypeSearchChange(e.target.value)}
            />
            <div className="add-routing-rule-study-options">{optionsContent}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const AddRoutingRuleModal: React.FC<AddRoutingRuleModalProps> = props => {
  return <AddRoutingRuleModalInner {...props} />;
};

function AddRoutingRuleModalInner({
  isOpen,
  activeStudyAssignmentRules = null,
  onClose,
  onSubmit,
}: Readonly<AddRoutingRuleModalProps>) {
  const [form, setForm] = useState<AddRoutingRuleFormValues>(DEFAULT_FORM);
  const [modalityOptions, setModalityOptions] = useState<ModalityOption[]>([]);
  const [radiologyTests, setRadiologyTests] = useState<RadiologyTestWithPrice[]>([]);
  const [hubFacilities, setHubFacilities] = useState<LinkedHubFacilityDetail[]>([]);
  const [spokeAtTitle, setSpokeAtTitle] = useState<string | null>(null);
  const [loadingModalities, setLoadingModalities] = useState(false);
  const [loadingTests, setLoadingTests] = useState(false);
  const [loadingHubs, setLoadingHubs] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [studyTypesOpen, setStudyTypesOpen] = useState(false);
  const [studyTypeSearch, setStudyTypeSearch] = useState('');

  const institutionOptions = useMemo(
    () => [
      { value: '', label: 'Any institution' },
      ...hubFacilities.map(hub => ({
        value: String(hub.id),
        label: hub.name,
      })),
    ],
    [hubFacilities]
  );

  const loadRadiologyTests = useCallback(async (modalityIds: number[]) => {
    const facilityId = authService.getFacilityId();
    if (!facilityId) {
      setRadiologyTests([]);
      setLoadError('Facility is not available for the current user.');
      return;
    }

    if (modalityIds.length === 0) {
      setRadiologyTests([]);
      setForm(prev => ({ ...prev, studyTypeIds: [] }));
      return;
    }

    setLoadingTests(true);
    setLoadError(null);

    const result = await getRadiologyTestsWithPrices(facilityId, {
      page: 1,
      limit: 200,
      ...(modalityIds.length === 1 ? { modality_id: modalityIds[0] } : {}),
    });

    if (!result.success || !result.data) {
      setRadiologyTests([]);
      setLoadError(result.error || 'Failed to load study types');
      setLoadingTests(false);
      return;
    }

    const tests = [...result.data].sort((left, right) =>
      left.test_name.localeCompare(right.test_name)
    );
    const testIds = new Set(tests.map(test => test.id));
    setRadiologyTests(tests);
    setForm(prev => ({
      ...prev,
      studyTypeIds: prev.studyTypeIds.filter(id => testIds.has(id)),
    }));
    setLoadingTests(false);
  }, []);

  const loadModalities = useCallback(async () => {
    setLoadingModalities(true);
    try {
      let stored = getModalities();
      if (!stored?.length) {
        await fetchAndStoreModalities();
        stored = getModalities();
      }
      setModalityOptions(getRoutingRuleModalityOptions(stored));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load modalities');
    } finally {
      setLoadingModalities(false);
    }
  }, []);

  const loadHubFacilities = useCallback(async () => {
    const facilityId = authService.getFacilityId();
    if (!facilityId) {
      setHubFacilities([]);
      setSpokeAtTitle(null);
      return;
    }

    setLoadingHubs(true);
    const result = await facilityService.getFacilityById(facilityId);
    if (result.success && result.data) {
      const facility = result.data;
      const hubDetails = facility.linked_hub_facility_details ?? [];
      const hubIds = facility.linked_hub_facility_ids ?? [];
      setHubFacilities(
        hubDetails.length > 0 ? hubDetails : hubIds.map(id => ({ id, name: `Hub ${id}` }))
      );
      setSpokeAtTitle(facility.pacs_ae_title?.trim() || null);
    } else {
      setHubFacilities([]);
      setSpokeAtTitle(null);
    }
    setLoadingHubs(false);
  }, []);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }
    const rafId = requestAnimationFrame(() => {
      const content = document.querySelector(
        '.add-routing-rule-dialog-content'
      ) as HTMLElement | null;
      if (content) {
        content.style.setProperty('z-index', String(Z_INDEX_CHANGE_PASSWORD_MODAL), 'important');
        const overlay = content.previousElementSibling as HTMLElement | null;
        if (overlay) {
          overlay.style.setProperty('z-index', String(Z_INDEX_CHANGE_PASSWORD_MODAL), 'important');
        }
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setForm(DEFAULT_FORM);
    setRadiologyTests([]);
    setLoadError(null);
    setStudyTypesOpen(false);
    setStudyTypeSearch('');
    loadModalities();
    loadHubFacilities();
  }, [isOpen, loadModalities, loadHubFacilities]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    void loadRadiologyTests(form.modalityIds);
  }, [isOpen, form.modalityIds, loadRadiologyTests]);

  const filteredRadiologyTests = useMemo(() => {
    const search = studyTypeSearch.trim().toLowerCase();
    if (!search) {
      return radiologyTests;
    }
    return radiologyTests.filter(test => test.test_name.toLowerCase().includes(search));
  }, [radiologyTests, studyTypeSearch]);

  const selectedStudyLabels = useMemo(() => {
    const selected = new Set(form.studyTypeIds);
    return radiologyTests.filter(test => selected.has(test.id)).map(test => test.test_name);
  }, [form.studyTypeIds, radiologyTests]);

  const updateForm = <K extends keyof AddRoutingRuleFormValues>(
    key: K,
    value: AddRoutingRuleFormValues[K]
  ) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const toggleModality = (modalityId: number) => {
    setForm(prev => ({
      ...prev,
      modalityIds: prev.modalityIds.includes(modalityId)
        ? prev.modalityIds.filter(id => id !== modalityId)
        : [...prev.modalityIds, modalityId],
    }));
  };

  const toggleStudyType = (testId: number) => {
    setForm(prev => {
      const selected = prev.studyTypeIds.includes(testId)
        ? prev.studyTypeIds.filter(id => id !== testId)
        : [...prev.studyTypeIds, testId];
      return { ...prev, studyTypeIds: selected };
    });
  };

  const buildPayload = (): CreateDicomRoutingRuleRequest => {
    const payload: CreateDicomRoutingRuleRequest = {
      name: form.name.trim(),
      modality_ids: form.modalityIds,
      destination_type: form.destinationType,
      destination_ae: form.destinationAe.trim(),
      is_active: form.active,
    };

    if (form.studyTypeIds.length > 0) {
      payload.radiology_test_ids = form.studyTypeIds;
    }
    if (form.aeTitle.trim()) {
      payload.match_ae_title = form.aeTitle.trim();
    }
    if (form.hubFacilityId != null) {
      payload.hub_facility_id = form.hubFacilityId;
    }
    if (form.matchPriority.trim()) {
      payload.match_priority = form.matchPriority.trim();
    }
    if (spokeAtTitle) {
      payload.at_title = spokeAtTitle;
    }

    return payload;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setLoadError('Rule name is required.');
      return;
    }
    if (form.modalityIds.length === 0) {
      setLoadError('Select at least one modality.');
      return;
    }
    if (!form.destinationAe.trim()) {
      setLoadError('Destination AE / node is required.');
      return;
    }

    if (hasPrefetchedCrossRuleValidationData(activeStudyAssignmentRules)) {
      const modalityConflict = findRoutingRuleModalityConflict(
        form.modalityIds,
        activeStudyAssignmentRules,
        modalityOptions,
        form.active
      );
      if (modalityConflict) {
        setLoadError(modalityConflict);
        return;
      }
    }

    setIsSubmitting(true);
    setLoadError(null);
    try {
      const hubFacilityName = hubFacilities.find(hub => hub.id === form.hubFacilityId)?.name;
      await onSubmit(buildPayload(), {
        studyTypeLabels: selectedStudyLabels,
        hubFacilityName,
      });
      onClose();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to create routing rule');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => !open && onClose()}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
    >
      <DialogContent
        unstyled
        showCloseButton={false}
        className="add-routing-rule-dialog-content"
      >
        <button
          type="button"
          className="add-routing-rule-close-btn"
          onClick={onClose}
          aria-label="Close dialog"
        >
          <X
            strokeWidth={ICON_STROKE_WIDTH}
            size={20}
          />
        </button>

        <header className="add-routing-rule-header">
          <h2 className="add-routing-rule-title">Add routing rule</h2>
          <p className="add-routing-rule-subtitle">
            Tell the DICOM listener where to send incoming studies that match these criteria.
          </p>
        </header>

        <form
          className="add-routing-rule-form"
          onSubmit={handleSubmit}
        >
          <div className="add-routing-rule-form-body">
            {loadError ? (
              <p
                className="add-routing-rule-error"
                role="alert"
              >
                {loadError}
              </p>
            ) : null}

            <div className="add-routing-rule-field">
              <label
                className="add-routing-rule-label"
                htmlFor="routing-rule-name"
              >
                Rule name
              </label>
              <input
                id="routing-rule-name"
                type="text"
                className="add-routing-rule-input"
                placeholder="e.g. CT — PACS Primary"
                value={form.name}
                onChange={e => updateForm('name', e.target.value)}
              />
            </div>

            <div className="add-routing-rule-field">
              <span className="add-routing-rule-label">Match modality</span>
              <div className="add-routing-rule-modality-pills">
                <RoutingModalityPills
                  loadingModalities={loadingModalities}
                  modalityOptions={modalityOptions}
                  selectedModalityIds={form.modalityIds}
                  onToggle={toggleModality}
                />
              </div>
            </div>

            <RoutingStudyTypesField
              studyTypesOpen={studyTypesOpen}
              onToggleOpen={() => setStudyTypesOpen(open => !open)}
              selectedStudyLabels={selectedStudyLabels}
              studyTypeSearch={studyTypeSearch}
              onStudyTypeSearchChange={setStudyTypeSearch}
              loadingTests={loadingTests}
              modalityIds={form.modalityIds}
              filteredRadiologyTests={filteredRadiologyTests}
              selectedStudyTypeIds={form.studyTypeIds}
              onToggleStudyType={toggleStudyType}
            />

            <div className="add-routing-rule-grid add-routing-rule-grid--two">
              <div className="add-routing-rule-field">
                <label
                  className="add-routing-rule-label"
                  htmlFor="routing-rule-ae-title"
                >
                  AE title <span className="add-routing-rule-optional">(optional)</span>
                </label>
                <input
                  id="routing-rule-ae-title"
                  type="text"
                  className="add-routing-rule-input"
                  placeholder="SCANNER_AE"
                  value={form.aeTitle}
                  onChange={e => updateForm('aeTitle', e.target.value)}
                />
              </div>
              <div className="add-routing-rule-field">
                <label
                  className="add-routing-rule-label"
                  htmlFor="routing-rule-institution"
                >
                  Institution / facility{' '}
                  <span className="add-routing-rule-optional">(optional)</span>
                </label>
                <select
                  id="routing-rule-institution"
                  className="add-routing-rule-select"
                  value={form.hubFacilityId != null ? String(form.hubFacilityId) : ''}
                  disabled={loadingHubs}
                  onChange={e =>
                    updateForm('hubFacilityId', e.target.value ? Number(e.target.value) : null)
                  }
                >
                  {loadingHubs ? (
                    <option value="">Loading institutions…</option>
                  ) : (
                    institutionOptions.map(option => (
                      <option
                        key={option.value || 'any'}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            <div className="add-routing-rule-field">
              <label
                className="add-routing-rule-label"
                htmlFor="routing-rule-priority"
              >
                Match priority
              </label>
              <select
                id="routing-rule-priority"
                className="add-routing-rule-select"
                value={form.matchPriority}
                onChange={e => updateForm('matchPriority', e.target.value)}
              >
                {MATCH_PRIORITY_OPTIONS.map(option => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="add-routing-rule-grid add-routing-rule-grid--two">
              <div className="add-routing-rule-field">
                <label
                  className="add-routing-rule-label"
                  htmlFor="routing-rule-destination-type"
                >
                  Destination type
                </label>
                <select
                  id="routing-rule-destination-type"
                  className="add-routing-rule-select"
                  value={form.destinationType}
                  onChange={e => updateForm('destinationType', e.target.value)}
                >
                  {DESTINATION_TYPE_OPTIONS.map(option => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="add-routing-rule-field">
                <label
                  className="add-routing-rule-label"
                  htmlFor="routing-rule-destination-ae"
                >
                  Destination AE / node
                </label>
                <input
                  id="routing-rule-destination-ae"
                  type="text"
                  className="add-routing-rule-input"
                  placeholder="PACS_PRIMARY"
                  value={form.destinationAe}
                  onChange={e => updateForm('destinationAe', e.target.value)}
                />
              </div>
            </div>
          </div>

          <footer className="add-routing-rule-footer">
            <div className="add-routing-rule-active">
              <ToggleSwitch
                checked={form.active}
                onChange={checked => updateForm('active', checked)}
              />
              <span>Active</span>
            </div>
            <div className="add-routing-rule-footer-actions">
              <button
                type="button"
                className="add-routing-rule-btn secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="add-routing-rule-btn primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Adding…' : 'Add rule'}
              </button>
            </div>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddRoutingRuleModal;
